import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabasePublic } from '../lib/supabasePublic';
import { 
  QrCode, 
  Copy, 
  Check, 
  DollarSign, 
  User, 
  CreditCard, 
  Trophy,
  MessageSquare,
  Calendar,
  Share2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import QRCode from 'qrcode';

// Funções utilitárias para geração do PIX EMV (mesmas do PixQRModal)
const calculateCRC16 = (data: string): string => {
  const polynomial = 0x1021;
  let crc = 0xFFFF;

  for (let i = 0; i < data.length; i++) {
    crc ^= (data.charCodeAt(i) << 8);
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ polynomial;
      } else {
        crc <<= 1;
      }
      crc &= 0xFFFF;
    }
  }

  return crc.toString(16).toUpperCase().padStart(4, "0");
};

const createEMVField = (id: string, value: string): string => {
  const length = value.length.toString().padStart(2, "0");
  return `${id}${length}${value}`;
};

const formatPixKeyForEMV = (pixKey: string, pixKeyType: string): string => {
  switch (pixKeyType) {
    case 'cpf':
    case 'cnpj':
      return pixKey.replace(/\D/g, '');
    case 'phone':
      const cleanPhone = pixKey.replace(/\D/g, '');
      if (cleanPhone.startsWith('55') && cleanPhone.length === 13) {
        return `+${cleanPhone}`;
      } else if (cleanPhone.length === 11) {
        return `+55${cleanPhone}`;
      } else if (cleanPhone.length === 10) {
        return `+55${cleanPhone}`;
      }
      return `+55${cleanPhone}`;
    case 'email':
    default:
      return pixKey.toLowerCase().trim();
  }
};

const generatePixEMV = (
  pixKey: string,
  pixKeyType: string,
  holderName: string,
  amount: number
): string => {
  const formattedPixKey = formatPixKeyForEMV(pixKey, pixKeyType);
  const cleanHolderName = holderName
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .substring(0, 25);

  let payload = "";

  // 00 - Payload Format Indicator
  payload += createEMVField("00", "01");

  // 26 - Merchant Account Information
  let merchantAccount = "";
  merchantAccount += createEMVField("00", "BR.GOV.BCB.PIX");
  merchantAccount += createEMVField("01", formattedPixKey);
  payload += createEMVField("26", merchantAccount);

  // 52 - Merchant Category Code
  payload += createEMVField("52", "0000");

  // 53 - Transaction Currency (986 = BRL)
  payload += createEMVField("53", "986");

  // 54 - Transaction Amount
  if (amount > 0) {
    payload += createEMVField("54", amount.toFixed(2));
  }

  // 58 - Country Code
  payload += createEMVField("58", "BR");

  // 59 - Merchant Name
  payload += createEMVField("59", cleanHolderName);

  // 60 - Merchant City
  payload += createEMVField("60", "SAO PAULO");

  // 62 - Additional Data Field Template
  const txid = `TXN${Date.now().toString().slice(-8)}`;
  const additionalData = createEMVField("05", txid);
  payload += createEMVField("62", additionalData);

  // 63 - CRC16 placeholder
  payload += "6304";

  // Calcula e adiciona CRC16
  const crc16 = calculateCRC16(payload);
  return payload + crc16;
};

interface PixConfig {
  pix_key: string;
  pix_key_type: string;
  pix_holder_name: string;
  valor_padrao: number;
  pix_public_token: string;
  pix_whatsapp: string | null;
  pix_whatsapp_country_code: string | null;
}

interface Group {
  id: string;
  nome: string;
}

const PixPaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pixConfig, setPixConfig] = useState<PixConfig | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [pixPayload, setPixPayload] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [playerName, setPlayerName] = useState('');

  const currentMonth = new Date().toLocaleDateString('pt-BR', { 
    month: 'long', 
    year: 'numeric' 
  });

  const formatPixKey = (key: string, type: string) => {
    switch (type) {
      case 'cpf':
        const cpfDigits = key.replace(/\D/g, '');
        return cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      case 'cnpj':
        const cnpjDigits = key.replace(/\D/g, '');
        return cnpjDigits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      case 'phone':
        const phoneDigits = key.replace(/\D/g, '');
        if (phoneDigits.length === 11) {
          return phoneDigits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (phoneDigits.length === 10) {
          return phoneDigits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return key;
      default:
        return key;
    }
  };

  const generateQRCode = async () => {
    if (!pixConfig) return;

    try {
      const payload = generatePixEMV(
        pixConfig.pix_key,
        pixConfig.pix_key_type,
        pixConfig.pix_holder_name,
        pixConfig.valor_padrao
      );

      setPixPayload(payload);

      const qrUrl = await QRCode.toDataURL(payload, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'M'
      });

      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
    }
  };

  useEffect(() => {
    const fetchPixData = async () => {
      if (!token) {
        setError('Token de pagamento não fornecido');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Buscando configuração PIX com token:', token);
        
        // Buscar configuração PIX pelo token público
        const { data: configData, error: configError } = await supabasePublic
          .from('grupo_configuracoes')
          .select('pix_key, pix_key_type, pix_holder_name, valor_padrao, pix_public_token, pix_whatsapp, pix_whatsapp_country_code, grupo_id')
          .eq('pix_public_token', token)
          .maybeSingle();

        if (configError) {
          console.error('❌ Erro ao buscar configuração PIX:', configError);
          if (configError.code === 'PGRST116') {
            setError('Página de pagamento não encontrada ou token inválido');
          } else {
            throw configError;
          }
          setLoading(false);
          return;
        }

        if (!configData) {
          console.warn('⚠️ Configuração PIX não encontrada para token:', token);
          setError('Configuração de pagamento não encontrada');
          setLoading(false);
          return;
        }

        console.log('✅ Configuração PIX encontrada:', {
          pix_key: configData.pix_key ? '***' : null,
          pix_key_type: configData.pix_key_type,
          pix_holder_name: configData.pix_holder_name,
          valor_padrao: configData.valor_padrao,
          grupo_id: configData.grupo_id
        });

        // Verificar se todos os dados PIX estão completos
        if (!configData.pix_key || !configData.pix_holder_name || !configData.valor_padrao) {
          console.error('❌ Configuração PIX incompleta:', {
            has_pix_key: !!configData.pix_key,
            has_holder_name: !!configData.pix_holder_name,
            has_valor_padrao: !!configData.valor_padrao
          });
          setError('Configuração PIX incompleta');
          setLoading(false);
          return;
        }

        // Buscar dados do grupo separadamente
        console.log('🔍 Buscando dados do grupo:', configData.grupo_id);
        const { data: groupData, error: groupError } = await supabasePublic
          .from('grupos')
          .select('id, nome')
          .eq('id', configData.grupo_id)
          .single();

        if (groupError) {
          console.error('❌ Erro ao buscar dados do grupo:', groupError);
          throw groupError;
        }

        if (!groupData) {
          console.warn('⚠️ Grupo não encontrado:', configData.grupo_id);
          setError('Grupo não encontrado');
          setLoading(false);
          return;
        }

        console.log('✅ Dados do grupo encontrados:', groupData);

        setPixConfig(configData);
        setGroup(groupData);
        
        // Gerar QR Code
        await generateQRCode();
      } catch (error: any) {
        console.error('❌ Erro geral ao carregar dados PIX:', error);
        console.error('Error fetching PIX data:', error);
        setError('Erro ao carregar dados de pagamento');
      } finally {
        setLoading(false);
      }
    };

    fetchPixData();
  }, [token]);

  useEffect(() => {
    if (pixConfig) {
      generateQRCode();
    }
  }, [pixConfig]);

  const copyPixPayload = async () => {
    try {
      await navigator.clipboard.writeText(pixPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar payload:', error);
    }
  };

  const copyPixInfo = async () => {
    const description = playerName 
      ? `Mensalidade ${currentMonth} - ${playerName}`
      : `Mensalidade ${currentMonth}`;

    const pixInfo = `*PIX - ${group?.nome}*

*Mensalidade de ${currentMonth}*
*Valor:* ${pixConfig!.valor_padrao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
*Chave PIX:* ${formatPixKey(pixConfig!.pix_key, pixConfig!.pix_key_type)}
*Titular:* ${pixConfig!.pix_holder_name}
*Descrição sugerida:* ${description}

_Após fazer o PIX, envie o comprovante via WhatsApp_`;

    try {
      await navigator.clipboard.writeText(pixInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar info PIX:', error);
    }
  };

  const openWhatsApp = () => {
    // Verificar se WhatsApp está configurado
    if (!pixConfig?.pix_whatsapp) {
      alert('Número do WhatsApp não configurado para este grupo. Entre em contato com o responsável do grupo.');
      return;
    }
    
    // Construir número completo com código do país
    const countryCode = pixConfig.pix_whatsapp_country_code || '+55';
    const phoneNumber = pixConfig.pix_whatsapp.replace(/\D/g, ''); // Remove formatação
    const fullPhoneNumber = `${countryCode.replace('+', '')}${phoneNumber}`;
    
    const description = playerName 
      ? `Mensalidade ${currentMonth} - ${playerName}`
      : `Mensalidade ${currentMonth}`;

    const message = `Olá! Acabei de fazer o PIX da mensalidade de ${currentMonth} do ${group?.nome}.

*Dados do PIX realizado:*
*Valor:* ${pixConfig.valor_padrao!.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
*Chave PIX:* ${formatPixKey(pixConfig.pix_key, pixConfig.pix_key_type)}
*Titular:* ${pixConfig.pix_holder_name}
*Descrição:* ${description}

Segue o comprovante:`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${fullPhoneNumber}?text=${encodedMessage}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Carregando dados de pagamento...</p>
        </div>
      </div>
    );
  }

  if (error || !pixConfig || !group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark p-4">
        <div className="card p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Página de pagamento não encontrada</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error || 'A página de pagamento que você está procurando não existe ou foi removida.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark">
      {/* Header */}
      <div className="bg-white dark:bg-dark-paper shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <img 
              src="/logo-presida-horizontal.png"
              alt="Presida.Club"
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-2xl font-bold">Pagamento PIX</h1>
              <p className="text-gray-500 dark:text-gray-400">
                Mensalidade de {currentMonth}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Informações e QR Code */}
          <div className="space-y-6">
            {/* Informações do Grupo */}
            <div className="card p-6">
              <div className="flex items-center gap-3 text-primary-600 dark:text-primary-400 mb-4">
                <Trophy className="w-8 h-8" />
                <h2 className="text-xl font-bold">{group.nome}</h2>
              </div>
              
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h3 className="font-medium text-primary-600 dark:text-primary-400">
                    Mensalidade de {currentMonth}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Faça o pagamento da sua mensalidade usando o QR Code PIX abaixo. 
                  Após o pagamento, envie o comprovante via WhatsApp.
                </p>
              </div>
            </div>

            {/* QR Code */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4 text-center">QR Code PIX</h2>
              
              <div className="text-center mb-6">
                {qrCodeUrl ? (
                  <div className="w-64 h-64 mx-auto bg-white p-4 rounded-lg border shadow-sm">
                    <img src={qrCodeUrl} alt="QR Code PIX" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-64 h-64 mx-auto bg-gray-100 dark:bg-dark-light rounded-lg flex items-center justify-center">
                    <QrCode className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Escaneie o QR Code com seu app bancário ou use as informações ao lado
                </p>
                
                <button
                  onClick={copyPixPayload}
                  className="btn-outline w-full flex items-center justify-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Código copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copiar código PIX
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Dados do Pagamento */}
          <div className="space-y-6">
            {/* Informações de Pagamento */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4">Dados para Pagamento</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-500">Valor</p>
                    <p className="font-bold text-green-600 text-xl">
                      {pixConfig.valor_padrao.toLocaleString('pt-BR', { 
                        style: 'currency', 
                        currency: 'BRL' 
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Chave PIX ({pixConfig.pix_key_type.toUpperCase()})</p>
                    <p className="font-medium">{formatPixKey(pixConfig.pix_key, pixConfig.pix_key_type)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <User className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-500">Titular da Conta</p>
                    <p className="font-medium">{pixConfig.pix_holder_name}</p>
                  </div>
                </div>
              </div>

            
            </div>

            {/* Descrição Sugerida */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4">Descrição Sugerida</h2>
              
              <div className="mb-4">
                <label htmlFor="playerName" className="block text-sm font-medium mb-1">
                  Seu nome (opcional)
                </label>
                <input
                  type="text"
                  id="playerName"
                  className="input w-full"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Digite seu nome para personalizar a descrição"
                />
              </div>

              <div className="p-4 bg-gray-50 dark:bg-dark-light rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Use esta descrição no PIX:</p>
                <p className="font-medium">
                  {playerName 
                    ? `Mensalidade ${currentMonth} - ${playerName}`
                    : `Mensalidade ${currentMonth}`
                  }
                </p>
              </div>
            </div>

            {/* Enviar Comprovante */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4">Após o Pagamento</h2>
              
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-4">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <strong>Importante:</strong> Após fazer o PIX, envie o comprovante via WhatsApp 
                  para o responsável do grupo para confirmar seu pagamento.
                </p>
              </div>

              <button
                onClick={openWhatsApp}
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={!pixConfig?.pix_whatsapp}
              >
                <MessageSquare className="w-4 h-4" />
                {pixConfig?.pix_whatsapp 
                  ? `Enviar comprovante (${pixConfig.pix_whatsapp_country_code || '+55'} ${pixConfig.pix_whatsapp})`
                  : 'WhatsApp não configurado'
                }
              </button>
              
              {!pixConfig?.pix_whatsapp && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    <strong>Atenção:</strong> O responsável do grupo ainda não configurou o WhatsApp 
                    para recebimento de comprovantes. Entre em contato diretamente com ele.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-dark-paper border-t dark:border-dark-border mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
            <Trophy className="w-5 h-5" />
            <span className="font-medium">Presida.Club</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gestão financeira para times de futebol amador
          </p>
        </div>
      </div>
    </div>
  );
};

export default PixPaymentPage;